import { useState, useEffect, useRef, useCallback } from "react";
import { marked } from "marked";
import { getToken } from "../../utils";
import { streamV1AiFeatureChat } from "../../api-client/api.ai-feature/api";
import { Sparkles, X, SendHorizontal, TrendingUp, AlertTriangle, Clock } from "lucide-react";

const QUICK_QUESTIONS = [
  { label: "What are the key trends?", icon: TrendingUp },
  { label: "Any risks to watch?", icon: AlertTriangle },
  { label: "Top performers?", icon: Clock },
];

interface AIChatPanelProps {
  sessionId: string;
  onClose: () => void;
}

export function AIChatPanel({ sessionId, onClose }: AIChatPanelProps) {
  const [chatMessages, setChatMessages] = useState<{ id: number; role: "user" | "ai"; text: string }[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const userScrolledUp = useRef(false);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 40;
      userScrolledUp.current = !atBottom;
    };
    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!userScrolledUp.current && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, isTyping]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isTyping) return;
    setChatMessages(prev => [...prev, { id: Date.now(), role: "user" as const, text }]);
    setInputValue("");
    setIsTyping(true);

    let aiMsgId: number | null = null;

    const appendToken = (token: string) => {
      if (aiMsgId === null) {
        aiMsgId = Date.now() + 1;
        setIsTyping(false);
        setChatMessages(prev => [...prev, { id: aiMsgId!, role: "ai", text: token }]);
      } else {
        setChatMessages(prev => prev.map(m =>
          m.id === aiMsgId ? { ...m, text: m.text + token } : m
        ));
      }
    };

    try {
      await streamV1AiFeatureChat(
        { ...getToken(), session_id: sessionId, query: text },
        appendToken,
        () => { setIsTyping(false); },
        () => {
          setIsTyping(false);
          if (aiMsgId === null) {
            setChatMessages(prev => [...prev, { id: Date.now() + 1, role: "ai", text: "Sorry, I couldn't process your question. Please try again." }]);
          }
        },
        () => {
          setIsTyping(false);
          setChatMessages(prev => [...prev, { id: Date.now() + 1, role: "ai", text: "That question seems outside the scope of this report. Please ask something related to the report data." }]);
        }
      );
    } catch (err) {
      console.error("Chat error:", err);
      setIsTyping(false);
      setChatMessages(prev => [...prev, { id: Date.now() + 1, role: "ai", text: "Sorry, I couldn't process your question. Please try again." }]);
    }
  }, [isTyping, sessionId]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  }

  return (
    <div className="ai-summary-panel__chat">
      <div className="ai-summary-panel__chat-header">
        <div className="ai-summary-panel__chat-header-left">
          <Sparkles className="ai-summary-panel__chat-header-icon" />
          <span className="ai-summary-panel__chat-header-title">Ask about this report</span>
        </div>
        <button onClick={onClose} className="ai-summary-panel__chat-close-btn">
          <X />
        </button>
      </div>

      {chatMessages.length === 0 && (
        <div className="ai-summary-panel__chat-suggestions">
          <p className="ai-summary-panel__chat-suggestions-label">Suggested questions</p>
          <div className="ai-summary-panel__chat-chips">
            {QUICK_QUESTIONS.map((q) => (
              <button key={q.label} onClick={() => sendMessage(q.label)} className="ai-summary-panel__chat-chip">
                <q.icon className="ai-summary-panel__chat-chip-icon" />
                <span>{q.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {chatMessages.length > 0 && (
        <div ref={chatContainerRef} className="ai-summary-panel__chat-messages">
          {chatMessages.map((msg) => (
            <div key={msg.id} className={`ai-summary-panel__chat-message ai-summary-panel__chat-message--${msg.role}`}>
              {msg.role === "ai" && (
                <div className="ai-summary-panel__chat-ai-row">
                  <div className="ai-summary-panel__chat-ai-avatar"><Sparkles /></div>
                  <div
                    className="ai-summary-panel__chat-ai-text"
                    dangerouslySetInnerHTML={{ __html: marked.parse(msg.text || "") as string }}
                  />
                </div>
              )}
              {msg.role === "user" && (
                <div className="ai-summary-panel__chat-user-bubble"><p>{msg.text}</p></div>
              )}
            </div>
          ))}

          {isTyping && (
            <div className="ai-summary-panel__chat-typing">
              <div className="ai-summary-panel__chat-ai-avatar"><Sparkles /></div>
              <div className="ai-summary-panel__chat-typing-dots">
                <span /><span /><span />
              </div>
            </div>
          )}

          {chatMessages.length > 0 && !isTyping && chatMessages[chatMessages.length - 1].role === "ai" && (
            <div className="ai-summary-panel__chat-followups">
              {QUICK_QUESTIONS
                .filter(q => !chatMessages.some(m => m.role === "user" && m.text.toLowerCase() === q.label.toLowerCase()))
                .slice(0, 3)
                .map(q => (
                  <button key={q.label} onClick={() => sendMessage(q.label)} className="ai-summary-panel__chat-chip ai-summary-panel__chat-chip--small">
                    <q.icon className="ai-summary-panel__chat-chip-icon" />
                    <span>{q.label}</span>
                  </button>
                ))}
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      )}

      <div className="ai-summary-panel__chat-input-bar">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question about this report..."
          className="ai-summary-panel__chat-input"
          disabled={isTyping}
        />
        <button
          onClick={() => sendMessage(inputValue)}
          disabled={!inputValue.trim() || isTyping}
          className={`ai-summary-panel__chat-send-btn${inputValue.trim() && !isTyping ? " ai-summary-panel__chat-send-btn--active" : ""}`}
        >
          <SendHorizontal />
        </button>
      </div>
    </div>
  );
}
